'use client';

import { SelectInput } from '@/app/(intakeFormV2)/intake-v2/components/shared/SelectInput';
import { useOptionsForHDYHAU } from '@/app/(intakeFormV2)/intake-v2/components/steps/ContactInfoStep';
import { createLead } from '@/app/(intakeFormV2)/intake-v2/services/LeadService';
import { ContactDetails } from '@/app/(intakeFormV2)/intake-v2/types';
import { Checkbox } from '@/components/inputs/Checkbox';
import { Typography } from '@/components/ui/Text';
import { EMAIL_REGEX } from '@/utils/emailRegex';
import { TrackingHelper } from '@/utils/tracking';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { useIntakeWizardContext } from '../../contexts/WizardProvider';
import { useWizardNavigation } from '../../hooks/useWizardNavigation';
import { IntakeStepWrapper } from '../common/IntakeStepWrapper';
import { TextInput } from '../common/TextInput';

const MAX_NUMBER_OF_DIGITS = 10;

const SECOND_MAX_NUMBER_OF_DIGITS = 11;

const OTHER_REFERRAL_VALUE = 'Other';

const isValidPhoneNumber = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  return (
    cleaned.length === MAX_NUMBER_OF_DIGITS ||
    (cleaned.length === SECOND_MAX_NUMBER_OF_DIGITS && cleaned[0] === '1')
  );
};

export const ContactInfoStep = () => {
  const {
    state: {
      isSaving,
      leadId,
      error,
      formData: _formData,
      formData: { contact_details: contact },
    },
    dispatch,
  } = useIntakeWizardContext();

  const { options } = useOptionsForHDYHAU();

  const [formData, setFormData] = useState<ContactDetails>(() => {
    if (contact) {
      return {
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        sms_opt_in: contact.sms_opt_in,
        hdyhau: contact.hdyhau || undefined,
        hdyhau_other: contact.hdyhau_other || '',
      };
    }

    return { first_name: '', last_name: '', email: '', phone: '' };
  });

  const { goToNextStep, goToPreviousStep, hasPreviousStep } =
    useWizardNavigation();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }
    },
    [errors]
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.hdyhau?.trim() == 'Other' && !formData.hdyhau_other?.trim()) {
      newErrors.hdyhau_other = 'How did you hear about us is required';
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSelectChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const {
        target: { name, value },
      } = event;

      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));

      if (errors[name]) {
        setErrors(prev => {
          const next = { ...prev };

          delete next[name];

          return next;
        });
      }
    },
    [errors]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      // Update wizard state with contact details
      dispatch({
        type: 'SET_FORM_DATA',
        payload: { contact_details: formData },
      });

      // Create lead if we don't already have one
      if (!leadId) {
        try {
          dispatch({ type: 'SET_SAVING', payload: true });

          dispatch({ type: 'SET_ERROR', payload: null });

          const trackingData = TrackingHelper.getTrackingData();

          const leadId = await createLead(
            {
              ..._formData,
              contact_details: formData,
            },
            trackingData
          );

          dispatch({ type: 'SET_LEAD_ID', payload: leadId });
        } catch (error) {
          console.error('Error creating lead:', error);
          dispatch({
            type: 'SET_ERROR',
            payload: 'Failed to save your information. Please try again.',
          });
          return;
        } finally {
          dispatch({ type: 'SET_SAVING', payload: false });
        }
      }

      goToNextStep();
    },
    [validateForm, dispatch, formData, leadId, goToNextStep, _formData]
  );

  return (
    <IntakeStepWrapper
      heading="You're almost there!"
      description='Get your personalized quote in seconds, with a copy sent directly to your inbox.'
      submitLabel='Finish Details'
      onSubmit={handleSubmit}
      onBack={goToPreviousStep}
      showBack={hasPreviousStep}
      isSubmitting={isSaving}
    >
      <div className='flex flex-col gap-2.5'>
        {error && (
          <div className='rounded-md bg-red-50 p-3 text-sm text-red-600'>
            {error}
          </div>
        )}

        <div>
          <TextInput
            type='text'
            id='first_name'
            name='first_name'
            value={formData.first_name}
            onChange={handleInputChange}
            error={errors.first_name}
            placeholder='First Name'
          />

          {errors.first_name && (
            <p className='mt-1 text-sm text-red-500'>{errors.first_name}</p>
          )}
        </div>

        <div>
          <TextInput
            type='text'
            id='last_name'
            name='last_name'
            value={formData.last_name}
            onChange={handleInputChange}
            error={errors.last_name}
            placeholder='Last Name'
          />

          {errors.last_name && (
            <p className='mt-1 text-sm text-red-500'>{errors.last_name}</p>
          )}
        </div>

        <div>
          <TextInput
            type='email'
            id='email'
            name='email'
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            placeholder='Email'
          />
          {errors.email && (
            <p className='mt-1 text-sm text-red-500'>{errors.email}</p>
          )}
        </div>

        <div>
          <TextInput
            type='tel'
            id='phone'
            name='phone'
            value={formData.phone}
            onChange={handleInputChange}
            error={errors.phone}
            placeholder='Phone Number'
          />
          {errors.phone && (
            <p className='mt-1 text-sm text-red-500'>{errors.phone}</p>
          )}
        </div>

        <div>
          <SelectInput
            type='tel'
            id='hdyhau'
            name='hdyhau'
            value={formData.hdyhau}
            onChange={handleSelectChange}
            error={errors.hdyhau}
            placeholder='How did you hear about us?'
          >
            {/* // TODO SET THIS CORRECTLY */}
            <option key={'Please select an option'} value=''>
              How did you hear about us?
            </option>

            {options.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectInput>

          {errors.hdyhau && (
            <p className='mt-1 text-sm text-red-500'>{errors.hdyhau}</p>
          )}
        </div>

        {formData.hdyhau === OTHER_REFERRAL_VALUE && (
          <div>
            <TextInput
              type='text'
              id='hdyhau_other'
              name='hdyhau_other'
              value={formData.hdyhau_other}
              onChange={handleInputChange}
              error={errors.hdyhau_other}
              placeholder='How did you hear about us?'
            />

            {errors.hdyhau_other && (
              <p className='mt-1 text-sm text-red-500'>{errors.hdyhau_other}</p>
            )}
          </div>
        )}

        {/* SMS Consent */}
        <div>
          <Checkbox
            id='sms_opt_in'
            name='sms_opt_in'
            checked={formData.sms_opt_in ?? false}
            onChange={handleInputChange}
            label={
              <span className='text-sh-dark-gray leading-[130%]'>
                <Typography variant='body-small'>
                  By checking this box, I consent to receive marketing, customer
                  care, public service announcements, and account notifications
                  from Sollis Health. Reply STOP to opt-out; Reply HELP for
                  support; Message & data rates may apply; Messaging frequency
                  may vary. Visit our{' '}
                  <Link
                    href='https://www.sollishealth.com/privacy'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-[#986F34] underline'
                  >
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link
                    href='https://www.sollishealth.com/legal'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-[#986F34] underline'
                  >
                    Terms of Service
                  </Link>{' '}
                  for more information.
                </Typography>
              </span>
            }
          />

          {errors.sms_opt_in && (
            <p className='mt-1 text-sm text-red-500'>{errors.sms_opt_in}</p>
          )}
        </div>

        <div>
          <Typography variant='body-small'>
            By clicking &quot;Finish Details&quot;, you are agreeing to our{' '}
            <Link
              href='https://www.sollishealth.com/privacy'
              target='_blank'
              rel='noopener noreferrer'
              className='text-[#986F34] underline'
            >
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link
              href='https://www.sollishealth.com/legal'
              target='_blank'
              rel='noopener noreferrer'
              className='text-[#986F34] underline'
            >
              Terms of Service
            </Link>{' '}
            and agree to receive occasional email communications from Sollis.
            You can unsubscribe at any time.
          </Typography>
        </div>
      </div>
    </IntakeStepWrapper>
  );
};
